import PackagesApi from 'legacyfrontend/api/paquetes';
import ErrorSnackbar from 'legacyfrontend/components/ErrorSnackbar';
import Pagination from 'legacyfrontend/components/Pagination';
import LoadSpinner from 'legacyfrontend/components/spinner';
import PackageCard from 'legacyfrontend/components/packages/PackageCard';
import { PackageCardProvider } from 'legacyfrontend/contexts/PackageCardContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import Flex from '../../../components/Flex';
import { validatePackagesSearchFromUrlParams } from 'legacyfrontend/helpers/validatePackagesSearchSubmit';
import { useFilters } from '../../../contexts/FiltersContext';
import AsideFilters from './components/AsideFilters';

const INITIAL_PACKAGES_STATE = {
  isLoading: true,
  data: null,
  error: false,
  availableFilters: null,
};

const PackagesListView = () => {
  const { t } = useTranslation('layout');
  const { filters } = useFilters();
  const { search } = useLocation();
  const { onSearchCount, registerOnSearch } = useGlobal();
  const [packages, setPackages] = useState(INITIAL_PACKAGES_STATE);
  const [totalPages, setTotalPages] = useState();
  const [listToastOpen, setListToastOpen] = useState(false);
  const [listToastMessage, setListToastMessage] = useState('');

  const handleListToastClose = useCallback(() => {
    setListToastOpen(false);
  }, []);

  const filtersToSearchParams = useMemo(() => {
    if (!filters || !search) return '';

    return `&${new URLSearchParams(
      Object.fromEntries(Object.entries(filters).map(([key, { value }]) => [key, value]))
    ).toString()}`;
  }, [filters, search]);

  const hasValidSearchParams = useMemo(() => {
    if (!search) return false;

    const searchParams = new URLSearchParams(search);
    const hasOrigin = searchParams.get('originId') && searchParams.get('originName');
    const hasDestination = searchParams.get('destinationId') && searchParams.get('destinationName');
    const hasDates = searchParams.get('monthYear');

    return hasOrigin && hasDestination && hasDates;
  }, [search]);

  const fetchPackages = useCallback(async () => {
    if (!hasValidSearchParams) {
      setPackages({
        isLoading: false,
        data: [],
        error: false,
        availableFilters: null,
      });
      setTotalPages(0);
      setListToastOpen(false);
      return;
    }

    const urlParams = new URLSearchParams(search);
    const urlValidationError = validatePackagesSearchFromUrlParams({
      originId: urlParams.get('originId'),
      originName: urlParams.get('originName'),
      destinationId: urlParams.get('destinationId'),
      destinationName: urlParams.get('destinationName'),
      monthYear: urlParams.get('monthYear'),
      adultsQty: urlParams.get('adultsQty'),
    });
    if (urlValidationError) {
      setPackages({
        isLoading: false,
        data: [],
        error: false,
        availableFilters: null,
      });
      setTotalPages(0);
      setListToastMessage(urlValidationError.message);
      setListToastOpen(true);
      return;
    }

    setListToastOpen(false);

    if (onSearchCount === 0) {
      registerOnSearch();
    }

    try {
      setPackages({
        ...packages,
        isLoading: true,
      });
      isClient && window.scrollTo(0, 0);
      const query = search + filtersToSearchParams;
      const packageData = await PackagesApi.getPackagesV2(query);

      setPackages({
        isLoading: false,
        data: packageData || [],
        error: false,
        availableFilters: packageData?.availableFilters,
      });

      setTotalPages(packageData?.totalPages || 0);
      isClient && window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error(error);
      setPackages((prev) => ({
        ...prev,
        isLoading: false,
        error: true,
        data: [],
      }));
    }
  }, [search, filtersToSearchParams, onSearchCount, hasValidSearchParams, registerOnSearch]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const listToast = (
    <ErrorSnackbar message={listToastMessage} onClose={handleListToastClose} open={listToastOpen} />
  );

  if (onSearchCount === 0 && !hasValidSearchParams) {
    return (
      <>
        <div className='container package__list-view__area package__list-view__area--empty'>
          <h1>Busca paquetes disponibles</h1>
          <p style={{ textAlign: 'center', padding: '0 2rem' }}>
            Completa el formulario de búsqueda para encontrar los mejores paquetes
          </p>
        </div>
        {listToast}
      </>
    );
  }

  if (packages.isLoading) {
    return (
      <>
        <div
          className='container package__list-view__area loading-packages'
          style={{ textAlign: 'center', padding: '4rem 2rem' }}
        >
          <LoadSpinner />
          <h1 style={{ marginTop: '2rem' }}>{t('packages.searching')}</h1>
          <p style={{ marginTop: '1rem', color: '#666' }}>Esto puede tomar unos segundos...</p>
        </div>
        {listToast}
      </>
    );
  }

  if (!hasValidSearchParams) {
    return (
      <>
        <div className='container package__list-view__area package__list-view__area--empty'>
          <h1>{t('packages.no_results')}</h1>
          <p>Por favor, completa todos los campos de búsqueda para ver los paquetes disponibles.</p>
        </div>
        {listToast}
      </>
    );
  }

  if (packages.data?.length === 0 || !packages.data) {
    return (
      <>
        <div className='container package__list-view__area package__list-view__area--empty'>
          <h1>{t('packages.no_results')}</h1>
        </div>
        {listToast}
      </>
    );
  }

  return (
    <>
      <div className='package__list-view__area'>
        <h1 className='sr-only'>Resultados de búsqueda de paquetes</h1>
        <Flex className='package__list__view__wrapper--package'>
          {packages.availableFilters && <AsideFilters availableFilters={packages.availableFilters} />}
          <Flex className='package__list__view__wrapper--package__results' key={search} vertical>
            {packages.data.map((packageData) => (
              <PackageCardProvider
                key={`package-${packageData.id}`}
                packageData={packageData}
                query={search + filtersToSearchParams}
              >
                <PackageCard {...packageData} query={search + filtersToSearchParams} />
              </PackageCardProvider>
            ))}
          </Flex>
        </Flex>
        <Pagination totalPages={totalPages} />
      </div>
      {listToast}
    </>
  );
};

export default PackagesListView;
